import {
  Card,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
  DatePicker,
  Switch,
  Button,
  message,
} from "antd";
import React from "react";
import { DATE_FORMAT } from "../../../../../../../../utils/dateFormatUtils";
import {
  issuingAuthorityOptions,
  legalTypeOptions,
  licenseStatusOptions,
  licenseTypeOptions,
} from "../../../DummyData";
import DownloadDocIcon from "../../../../../../../../Icons/DownloadDocIcon";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import CustomLoaderIcon from "../../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";
import {
  updateDimensionAction,
  getSingleDimensionAction,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";

const { Text } = Typography;
const { TextArea } = Input;

const TradeLicenseDrawer = React.forwardRef(({ open, isCreateMode }, ref) => {
  const dispatch = useDispatch();
  const themeDefault = useSelector((state) => state.themeDefault);
  const singleEntityState = useSelector((state) => state.getSingleEntityList);
  const entityId = singleEntityState?.info?._id;
  const getSingleDimensionList = useSelector(
    (state) => state.getSingleDimensionList
  );
  const dimensionId = getSingleDimensionList?.info?.dimension?._id;
  const loadingSingle = useSelector(
    (state) => state.getSingleDimensionList?.loading
  );
  const [form] = Form.useForm();

  const submit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        entityId,
        type: "trade_license",
        name: values.tradeLicenseName,
        tradeLicenseNumber: values.tradeLicenseNumber,
        issueDate: values.issueDate?.format("YYYY-MM-DD") || null,
        expiryDate: values.expiryDate?.format("YYYY-MM-DD") || null,
        issuingAuthority: values.issuingAuthority,
        legalType: values.legalType,
        licenseType: values.licenseType,
        licenseStatus: values.licenseStatus,
        enablePreferred: Boolean(values.enablePreferred),
        internal_notes: values.internal_notes,
        fileAttachments: [],
      };

      // If update mode, dispatch update action
      if (!isCreateMode) {
        if (!dimensionId) {
          message.error("Dimension ID is required for update");
          throw new Error("Dimension ID is required for update");
        }
        await dispatch(updateDimensionAction(dimensionId, payload));
        message.success("Trade License updated successfully");
        // Refresh the dimension data to populate fields correctly
        await dispatch(getSingleDimensionAction(dimensionId));
        // Don't reset form - let parent handle step navigation
        // Return payload so parent can continue
        return payload;
      }

      form.resetFields();
      return payload;
    } catch (e) {
      console.error("Form validation failed:", e);
      if (!isCreateMode) {
        const errMsg = e?.message || e || "Failed to update trade license";
        message.error(errMsg);
        throw e;
      }
      throw e;
    }
  };

  React.useImperativeHandle(ref, () => ({ submit }));

  React.useEffect(() => {
    if (isCreateMode) return; // skip prefill in create mode
    const setting = getSingleDimensionList?.info?.setting || {};
    if (setting && Object.keys(setting).length > 0) {
      form?.setFieldsValue?.({
        tradeLicenseName: setting.name,
        tradeLicenseNumber: setting.trade_license_number,
        issueDate: setting.issue_date ? dayjs(setting.issue_date) : undefined,
        expiryDate: setting.expiry_date
          ? dayjs(setting.expiry_date)
          : undefined,
        issuingAuthority: setting.issuing_authority,
        legalType: setting.legal_type,
        licenseType: setting.license_type,
        licenseStatus: setting.license_status,
        enablePreferred: setting.enable_prefered,
        internal_notes: setting.internal_notes,
      });
    }
  }, [getSingleDimensionList, form, isCreateMode]);

  // Reset when opening without data (create mode)
  React.useEffect(() => {
    if (open && isCreateMode) {
      form?.resetFields?.();
      return;
    }
    const hasData = Boolean(
      getSingleDimensionList?.info?.setting &&
        Object.keys(getSingleDimensionList?.info?.setting || {}).length > 0
    );
    if (!hasData) {
      form?.resetFields?.();
    }
  }, [getSingleDimensionList, form, open, isCreateMode]);

  return (
    <Form
      form={form}
      name="tradeLicenseForm"
      autoComplete="off"
      layout="vertical"
    >
      {loadingSingle ? (
        <Flex align="center" justify="center">
          <CustomLoaderIcon />
        </Flex>
      ) : (
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Trade License Details
                </div>
              }
            >
              <Flex gap={16} wrap="wrap">
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    // changed to match BE schema
                    // name="tradeLicenseName"
                    name="tradeLicenseName"
                    label="Trade License Name"
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Input placeholder="Enter trade license name" />
                  </Form.Item>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="tradeLicenseNumber"
                    label="Trade License Number"
                    style={{ padding: 0, margin: 0, width: "100%" }}
                  >
                    <InputNumber
                      placeholder="Enter license number"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Flex>
              </Flex>

              <Flex gap={16} wrap="wrap">
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="issueDate"
                    label="Issue Date"
                    style={{ paddingTop: 8, margin: 0 }}
                  >
                    <DatePicker
                      placeholder="Select issue date"
                      style={{ width: "100%" }}
                      format={DATE_FORMAT}
                    />
                  </Form.Item>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="expiryDate"
                    label="Expiry Date"
                    style={{ paddingTop: 8, margin: 0 }}
                  >
                    <DatePicker
                      placeholder="Select expiry date"
                      style={{ width: "100%" }}
                      format={DATE_FORMAT}
                    />
                  </Form.Item>
                </Flex>
              </Flex>

              <Flex gap={16} wrap="wrap">
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="issuingAuthority"
                    label="Issuing Authority"
                    style={{ paddingTop: 8, margin: 0 }}
                  >
                    <Select
                      placeholder="Select issuing authority"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={issuingAuthorityOptions.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                    />
                  </Form.Item>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="legalType"
                    label="Legal Type"
                    style={{ paddingTop: 8, margin: 0 }}
                  >
                    <Select
                      placeholder="Select legal type"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={legalTypeOptions.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                    />
                  </Form.Item>
                </Flex>
              </Flex>

              <Flex gap={16} wrap="wrap">
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="licenseType"
                    label="License Type"
                    style={{ paddingTop: 8, paddingBottom: 8, margin: 0 }}
                  >
                    <Select
                      placeholder="Select license type"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={licenseTypeOptions.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                    />
                  </Form.Item>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="licenseStatus"
                    label="License Status"
                    style={{ paddingTop: 8, margin: 0 }}
                  >
                    <Select
                      placeholder="Select license status"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={licenseStatusOptions.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                    />
                  </Form.Item>
                </Flex>
              </Flex>
            </Card>
          </Col>

          <Col span={12}>
            <Card
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Additional Settings
                </div>
              }
            >
              <div style={{ marginBottom: 16 }}>
                <Flex align="center" gap={8}>
                  <Form.Item
                    name="enablePreferred"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                  <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                    Set as Preferred
                  </Text>
                </Flex>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    paddingTop: 4,
                    display: "block",
                  }}
                >
                  If enabled, the trade license will be pre-selected for any new
                  user that are added
                </Text>
              </div>
            </Card>

            {/* <Card
              style={{ marginTop: 16 }}
              bordered
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  File Attachments
                </div>
              }
            >
              <Flex vertical gap={8}>
                <Text style={{ fontSize: 16, fontWeight: 700 }}>
                  Upload a file(s) to link with this legal entity
                </Text>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Upload the relevant files for the legal entity
                </Text>
                <Button style={{ width: 150 }}>
                  <DownloadDocIcon size="small" />
                  Upload File
                </Button>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Only .xlsx, .xls, .csv files are supported
                </Text>
              </Flex>
            </Card> */}
          </Col>

          <Col span={24}>
            <Card
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Internal Notes (For Internal Use Only)
                </div>
              }
              style={{ marginTop: 16 }}
              bordered
            >
              <Form.Item name="internal_notes" label="Notes">
                <TextArea rows={4} placeholder="Enter Notes" />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      )}
    </Form>
  );
});

export default TradeLicenseDrawer;
